import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useDebounceValue } from "usehooks-ts";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, UserRoundPen, Users, X } from "lucide-react";
import { ACTIVE_STATUSES, GENDER } from "creso-core/constants";
import {
  investorProfileQueryOptions,
  useClients,
  useFetchKycStatus,
  useInvestorContactInfo,
  useInvestorProfile,
} from "creso-core/hooks";
import { findPrimaryHolder, SECOND_HOLDER_STEPS } from "creso-core/utils";
import { panSchema, SecondHolderPersonalInfoSchema } from "creso-core/zod";
import {
  Button,
  cn,
  Form,
  FormDatePicker,
  FormField,
  FormInput,
  FormSelect,
  useMultiStepForm,
} from "web-design-system";
import { AddSecondHolderFormData, Client } from "creso-core/types";
import { ClientSearchSelect } from "web-core";
import { handleError } from "@/utils/error";
import { KycStatusBadge } from "@crm/components/onboarding/kyc-status";
import { useSaveSecondHolderProfile } from "@crm/hooks/use-second-holder";

export function PersonalInfoStep({
  lastStepIndex,
  onExistingClientSaved,
}: {
  lastStepIndex: number;
  onExistingClientSaved?: () => void;
}) {
  const { formData, resetFormData, goToStep } =
    useMultiStepForm<AddSecondHolderFormData>();
  const {
    saveAndContinue,
    saveHolderProfile,
    isPending,
    secondHolder,
    clientId,
  } = useSaveSecondHolderProfile();
  const { mutateAsync: fetchKycStatus, isPending: isCheckingKyc } =
    useFetchKycStatus();
  const [kycResult, setKycResult] = useState<{
    kyc_status: "S" | "F" | "U";
    kyc_status_remark?: string | null;
    name?: string;
    pan: string;
  } | null>(null);
  const [isSelectingExistingClient, setIsSelectingExistingClient] =
    useState(false);
  const [existingClient, setExistingClient] = useState<Client | null>(null);

  // A client sharing this client's investor profile is the same person. Find it
  // in the client list by matching primary_profile_id against the current
  // client's investor_profile_guid (clients with the same PAN narrow the list).
  const { data: ownProfiles } = useQuery(
    investorProfileQueryOptions(
      { client_id: clientId },
      { enabled: clientId > 0 }
    )
  );
  const ownPrimaryProfile = findPrimaryHolder(ownProfiles);
  const { data: samePanClients } = useClients(
    { search: ownPrimaryProfile?.pan, pageSize: 50 },
    {
      enabled:
        !!ownPrimaryProfile?.investor_profile_guid && !!ownPrimaryProfile?.pan,
    }
  );
  const excludedClientIds = [
    ...new Set([
      ...(clientId ? [clientId] : []),
      ...(samePanClients?.results ?? [])
        .filter(
          (client) =>
            client.primary_profile_id ===
            ownPrimaryProfile?.investor_profile_guid
        )
        .map((client) => client.id),
    ]),
  ];

  const { data: existingProfile } = useInvestorProfile({
    client_id: existingClient?.id,
  });
  const { data: existingContact } = useInvestorContactInfo(
    existingClient?.investment_account ?? undefined
  );

  // Everything the picked existing client contributes across all 3 steps,
  // merged into this step's submit so it's persisted and seeded into the
  // shared wizard formData for Contact Details / Other Information to pick up.
  const existingClientData = existingClient
    ? {
        name: existingProfile?.name || existingClient.name,
        pan: existingProfile?.pan || existingClient.pan,
        gender: existingProfile?.gender ?? "",
        date_of_birth: existingProfile?.date_of_birth ?? "",
        kyc_status: existingClient.kyc_status,
        kyc_status_remark: existingClient.kyc_status_remark,
        number: existingContact?.number ?? "",
        isd: existingContact?.isd ?? "+91",
        email: existingContact?.email || existingClient.email || "",
        phone_belongs_to: existingContact?.phone_belongs_to ?? "self",
        email_belongs_to: existingContact?.email_belongs_to ?? "self",
        occupation: existingProfile?.occupation ?? "",
        source_of_wealth: existingProfile?.source_of_wealth ?? "",
        income_slab: existingProfile?.income_slab ?? "",
        pep_details: existingProfile?.pep_details ?? "",
      }
    : null;

  const form = useForm<z.infer<typeof SecondHolderPersonalInfoSchema>>({
    resolver: zodResolver(SecondHolderPersonalInfoSchema),
    values: {
      name:
        existingClientData?.name ??
        (kycResult?.name || undefined) ??
        formData.name ??
        secondHolder?.name ??
        "",
      pan: existingClientData?.pan ?? formData.pan ?? secondHolder?.pan ?? "",
      gender:
        existingClientData?.gender ??
        formData.gender ??
        secondHolder?.gender ??
        "",
      date_of_birth:
        existingClientData?.date_of_birth ??
        formData.date_of_birth ??
        secondHolder?.date_of_birth ??
        "",
    },
  });

  const panValue = form.watch("pan");
  const statusPan =
    kycResult?.pan ??
    existingClientData?.pan ??
    formData.pan ??
    secondHolder?.pan ??
    "";
  const panIsDirty = (panValue ?? "").toUpperCase() !== statusPan.toUpperCase();
  const kycStatus =
    kycResult?.kyc_status ??
    existingClientData?.kyc_status ??
    formData.kyc_status ??
    secondHolder?.kyc_status;
  const kycRemark =
    kycResult?.kyc_status_remark ??
    existingClientData?.kyc_status_remark ??
    formData.kyc_status_remark ??
    secondHolder?.kyc_status_remark;
  const showCheckKycButton =
    !panValue || panIsDirty || !kycStatus || kycStatus === "U" || isCheckingKyc;
  const isKycVerified = kycStatus === "S" && !panIsDirty;
  const isKycNotFound = kycStatus === "F" && !panIsDirty;
  const isLinkedToExistingClient =
    !!existingClientData ||
    !!formData.linked_client_id ||
    !!formData.has_other_client;
  // Picked from the client list now, or saved earlier as an existing client
  const isExistingClientHolder =
    !!existingClient || !!formData.has_other_client;
  const linkedValues = existingClientData ?? formData;
  // Existing client's values are read-only; missing ones can still be filled
  const lockIfFilled = (value?: string | null) =>
    isLinkedToExistingClient && !!value;

  const [debouncedPan] = useDebounceValue(panValue, 400);
  const isCheckingDuplicatePan =
    !isLinkedToExistingClient && panSchema.safeParse(debouncedPan).success;
  const { data: duplicatePanClients } = useClients(
    { search: debouncedPan, pageSize: 5 },
    { enabled: isCheckingDuplicatePan }
  );
  const hasDuplicatePan =
    isCheckingDuplicatePan &&
    debouncedPan === panValue &&
    !!duplicatePanClients?.results?.some(
      (client) =>
        client.pan?.toUpperCase() === debouncedPan?.toUpperCase() &&
        client.id !== clientId
    );

  const handleCheckKyc = async () => {
    const isValid = await form.trigger(["name", "pan"]);
    if (!isValid) return;

    const data = form.getValues();

    try {
      await saveHolderProfile(data);
      const result = await fetchKycStatus({ pan: data.pan });
      setKycResult({ ...result, pan: data.pan });
      if (result.name) {
        form.setValue("name", result.name, { shouldDirty: false });
      }
    } catch (error) {
      handleError(error, "Failed to check KYC status");
    }
  };

  const onSubmit = async (
    data: z.infer<typeof SecondHolderPersonalInfoSchema>
  ) => {
    const stepData: Partial<AddSecondHolderFormData> = {
      ...data,
      kyc_status: kycStatus,
      kyc_status_remark: kycRemark ?? null,
      ...(existingClientData
        ? {
            linked_client_id: existingClient?.id,
            investor_profile_guid: existingProfile?.investor_profile_guid,
            number: existingClientData.number,
            isd: existingClientData.isd,
            email: existingClientData.email,
            phone_belongs_to: existingClientData.phone_belongs_to,
            email_belongs_to: existingClientData.email_belongs_to,
            occupation: existingClientData.occupation,
            source_of_wealth: existingClientData.source_of_wealth,
            income_slab: existingClientData.income_slab,
            pep_details: existingClientData.pep_details,
          }
        : {}),
    };
    const saved = await saveAndContinue(stepData);
    if (!saved || !isExistingClientHolder) return;
    // Skip ahead only if the existing client already fills the remaining
    // steps; otherwise continue so the missing fields can be entered.
    const merged = { ...formData, ...stepData };
    const remainingStepsComplete = SECOND_HOLDER_STEPS.filter(
      (step) => step.id === "contact-details" || step.id === "other-info"
    ).every((step) => step.completed(merged));
    if (!remainingStepsComplete) return;
    if (onExistingClientSaved) onExistingClientSaved();
    else goToStep(lastStepIndex);
  };

  return (
    <Form {...form}>
      <form
        className="flex min-h-0 flex-1 flex-col gap-6 pt-3"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted p-3">
          <div className="flex items-center gap-3">
            <Users className="size-4" />
            <span className="text-sm font-semibold">
              {isSelectingExistingClient || isExistingClientHolder
                ? "Second holder not in your client list?"
                : "Second holder already your client?"}
            </span>
          </div>
          <button
            type="button"
            className="text-sm font-medium underline"
            onClick={() => {
              if (isSelectingExistingClient || isExistingClientHolder) {
                setIsSelectingExistingClient(false);
                if (isExistingClientHolder) {
                  setExistingClient(null);
                  setKycResult(null);
                  resetFormData({});
                }
              } else {
                setIsSelectingExistingClient(true);
              }
            }}
          >
            {isSelectingExistingClient || isExistingClientHolder
              ? "Enter details manually"
              : "Select existing client"}
          </button>
        </div>
        {isSelectingExistingClient ? (
          <>
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">
                Personal Information
              </span>
              {isExistingClientHolder ? (
                <button
                  type="button"
                  aria-label="Enter details manually"
                  className="text-muted-foreground"
                  onClick={() => setIsSelectingExistingClient(false)}
                >
                  <X className="size-4" />
                </button>
              ) : null}
            </div>
            <ClientSearchSelect
              onSelect={(client) => {
                setExistingClient(client);
                setIsSelectingExistingClient(false);
              }}
              placeholder="Search for Client or PAN"
              excludeIds={excludedClientIds}
              clientsFilter={{
                status: ACTIVE_STATUSES,
                holding_pattern: ["single"],
              }}
            />
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">
                Personal Information
              </span>
              {isExistingClientHolder ? (
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-medium"
                  onClick={() => setIsSelectingExistingClient(true)}
                >
                  <UserRoundPen className="size-4" />
                  Select another
                </button>
              ) : null}
            </div>
            <div className="flex flex-col gap-10">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Name"
                      disabled={isKycVerified}
                    />
                  )}
                />
                <div className="flex items-start gap-2">
                  <FormField
                    control={form.control}
                    name="pan"
                    className="flex-1"
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        label="PAN"
                        disabled={lockIfFilled(linkedValues.pan)}
                        onChange={(val) => {
                          setKycResult(null);
                          field.onChange(val?.toUpperCase() ?? "");
                        }}
                      />
                    )}
                  />
                  <div className="shrink-0 pt-5">
                    {showCheckKycButton ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isCheckingKyc || isPending || !panValue}
                        onClick={handleCheckKyc}
                      >
                        <Loader2
                          className={cn("size-4 animate-spin", {
                            hidden: !isCheckingKyc,
                          })}
                        />
                        Check KYC
                      </Button>
                    ) : (
                      <KycStatusBadge
                        kyc_status={kycStatus}
                        remark={kycRemark}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormSelect
                      key={field.value}
                      {...field}
                      label="Gender"
                      options={GENDER}
                      disabled={lockIfFilled(linkedValues.gender)}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="date_of_birth"
                  render={({ field }) => (
                    <FormDatePicker
                      label="Date of birth"
                      format="yyyy-MM-dd"
                      value={field.value}
                      onChange={field.onChange}
                      fieldDisabled={lockIfFilled(linkedValues.date_of_birth)}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date > today;
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </>
        )}
        <div className="mt-auto flex flex-col gap-4">
          {isKycNotFound ? (
            <div className="rounded-lg border border-border p-3">
              <span className="text-xs font-semibold text-destructive">
                KYC not found.
              </span>{" "}
              <span className="text-xs text-foreground">
                Please complete KYC and return to add a second holder.
              </span>
            </div>
          ) : null}
          {hasDuplicatePan ? (
            <div className="rounded-lg border border-border p-3 flex gap-1">
              <span className="text-xs font-semibold text-foreground">
                PAN already in your client list.
              </span>
              <span className="text-xs text-foreground">
                Select from existing clients to add as second holder
              </span>
            </div>
          ) : null}
          <div className="flex justify-end">
            {isSelectingExistingClient ? (
              <Button type="button" disabled>
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={!isKycVerified || isPending || hasDuplicatePan}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
}
